import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

var CREDIT_CARDS;
(function (CREDIT_CARDS) {
    CREDIT_CARDS["None"] = "none";
    CREDIT_CARDS["Visa"] = "visa";
    CREDIT_CARDS["Mastercard"] = "mast";
    CREDIT_CARDS["Discover"] = "disc";
    CREDIT_CARDS["AmericanExpress"] = "amex";
    CREDIT_CARDS["JapanCreditBureau"] = "jcb";
    CREDIT_CARDS["ChinaUnionPay"] = "cup";
    CREDIT_CARDS["DinersClub"] = "diners";
})(CREDIT_CARDS || (CREDIT_CARDS = {}));

class CreditCardValidationService {
    static IsValidCreditCardNumber(creditCardNumber, creditCardBinRanges) {
        // First make sure the number passes the Luhn check
        if (!CreditCardValidationService.passesLuhnCheck(creditCardNumber)) {
            return false;
        }
        return CreditCardValidationService.GetBinRangeForCreditCard(creditCardNumber, creditCardBinRanges).IsValid;
    }
    static GetCreditCardType(creditCardNumber, creditCardBinRanges) {
        // First get the bin range
        const binRange = CreditCardValidationService.GetBinRangeForCreditCard(creditCardNumber, creditCardBinRanges, true);
        if (binRange.IsValid) {
            if (binRange.CardType) {
                return binRange.CardType;
            }
            else {
                return CREDIT_CARDS.None;
            }
        }
        return CREDIT_CARDS.None;
    }
    static GetBinRangeForCreditCard(creditCardNumber, creditCardBinRanges, requiredCardTypeOnly = false) {
        const formattedNumber = creditCardNumber.replace(/\D+/g, '');
        const possibleBinRanges = creditCardBinRanges?.ValidBinRanges.filter((binRange) => {
            const binToVerify = formattedNumber.substring(0, binRange.BinLength);
            if (Number.isNaN(binToVerify)) {
                return false;
            }
            const ccBinNumber = Number.parseInt(binToVerify, 10);
            const isValidBin = ccBinNumber >= Number.parseInt(binRange.From, 10) && ccBinNumber <= Number.parseInt(binRange.To, 10);
            if (requiredCardTypeOnly) {
                return isValidBin;
            }
            else {
                const isValidLength = formattedNumber.length >= binRange.CreditCardMinLength && formattedNumber.length <= binRange.CreditCardMaxLength;
                return isValidBin && isValidLength;
            }
        });
        if (possibleBinRanges && possibleBinRanges.length === 1) {
            return {
                IsValid: true,
                CardType: possibleBinRanges[0].CreditCardTypeString
            };
        }
        return {
            IsValid: false,
        };
    }
    // Logic stolen from old Resident portal and Luhn logic here https://en.wikipedia.org/wiki/Luhn_algorithm
    static passesLuhnCheck(creditCardNumber) {
        const formattedNumber = creditCardNumber.replace(/\D+/g, '');
        // Make sure the numbers aren't all 0s
        let nonZeroFound = false, nCheck = 0, bEven = false;
        for (let cardNumberIndex = formattedNumber.length - 1; cardNumberIndex >= 0; cardNumberIndex--) {
            const cDigit = formattedNumber.charAt(cardNumberIndex);
            let nDigit = parseInt(cDigit, 10);
            if (nDigit !== 0) {
                nonZeroFound = true;
            }
            if (bEven) {
                // eslint-disable-next-line no-cond-assign
                if ((nDigit *= 2) > 9) {
                    nDigit -= 9;
                }
            }
            nCheck += nDigit;
            bEven = !bEven;
        }
        if (!nonZeroFound) {
            return false;
        }
        return (nCheck % 10) === 0;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CreditCardValidationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CreditCardValidationService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CreditCardValidationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class RequestInterceptor {
    intercept(req, next) {
        let sanitizedRequest = req;
        sanitizedRequest = this.sanitizeParams(sanitizedRequest);
        sanitizedRequest = this.sanitizeHeaders(sanitizedRequest);
        sanitizedRequest = this.sanitizeQueryString(sanitizedRequest);
        return next.handle(sanitizedRequest);
    }
    sanitizeHeaders(req) {
        let headers = req.headers;
        req.headers.keys().forEach(key => {
            const value = req.headers.get(key);
            if (value === '' || value === 'null' || value === 'undefined') {
                headers = headers.delete(key);
            }
        });
        return req.clone({ headers });
    }
    sanitizeParams(req) {
        if (!req.params) {
            return req;
        }
        let params = req.params;
        req.params.keys().forEach(key => {
            const value = req.params.get(key);
            if (value === '' || value === 'null' || value === 'undefined') {
                params = params.delete(key);
            }
        });
        return req.clone({ params });
    }
    sanitizeQueryString(req) {
        const queryIndex = req.url.indexOf('?');
        if (queryIndex < 0) {
            return req;
        }
        const path = req.url.substring(0, queryIndex);
        const queryString = req.url.substring(queryIndex + 1)
            .split('&')
            .filter(param => param.indexOf('=') !== param.length - 1)
            .filter(param => !param.endsWith('=null') && !param.endsWith('=undefined'))
            .join('&');
        return req.clone({ url: `${path}?${queryString}` });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RequestInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RequestInterceptor });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RequestInterceptor, decorators: [{
            type: Injectable
        }] });

class BdxHttpModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BdxHttpModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: BdxHttpModule, imports: [HttpClientModule] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BdxHttpModule, providers: [
            {
                provide: HTTP_INTERCEPTORS,
                useClass: RequestInterceptor,
                multi: true
            }
        ], imports: [HttpClientModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BdxHttpModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HttpClientModule
                    ],
                    providers: [
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: RequestInterceptor,
                            multi: true
                        }
                    ]
                }]
        }] });

class ErrorHash {
    // Hash function was repurposed from here http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
    hashErrorString(value) {
        let hash = 0, i, chr, len;
        if (value.length === 0)
            return hash;
        for (i = 0, len = value.length; i < len; i++) {
            chr = value.charCodeAt(i);
            // eslint-disable-next-line no-bitwise
            hash = ((hash << 5) - hash) + chr;
            // eslint-disable-next-line no-bitwise
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ErrorHash, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ErrorHash, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ErrorHash, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/*
 * Public API Surface of ngx-app-utility
 */
// NOTE Keep alphabetical

/**
 * Generated bundle index. Do not edit.
 */

export { BdxHttpModule, CreditCardValidationService, ErrorHash };
